import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import {
  AuthModel,
  MfaChallengeModel,
  VerifyMfaRequest,
} from '../../interfaces/auth/auth.model';
import {
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../../interfaces/auth/forgot-password.model';
import { UserModel } from '../../interfaces/auth/user.model';
import { API_URL } from '../../utils/injection-tokens/api-url.token';
import { APP_NAME } from '../../utils/injection-tokens/app-name.token';
import { User } from '../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  private apiUrl = inject(API_URL);
  private appName = inject(APP_NAME, { optional: true });
  http: HttpClient;

  API_USERS_URL = `${this.apiUrl}users`;

  /**
   * Sign-in endpoints. The shared `users/login` of the CoReact API rejects external investigators (403),
   * so the Investigator Portal signs in through its own `investigator-portal/auth` endpoints.
   * Only the sign-in changes: `users/me` and `users/refresh-token` stay shared, and the backend explicitly allows
   * external investigators on them (Program.cs, IsOwnAccountPath).
   */
  private readonly API_LOGIN_URL =
    this.appName === 'investigator-portal' ? `${this.apiUrl}investigator-portal/auth` : this.API_USERS_URL;

  constructor() {
    const httpBackend = inject(HttpBackend);
    this.http = new HttpClient(httpBackend);
  }

  login(Email: string, Password: string): Observable<AuthModel | MfaChallengeModel> {
    return this.http.post<AuthModel | MfaChallengeModel>(`${this.API_LOGIN_URL}/login`, {
      Email,
      Password,
    });
  }

  verifyMfa(request: VerifyMfaRequest): Observable<AuthModel> {
    return this.http.post<AuthModel>(`${this.API_LOGIN_URL}/login/mfa`, {
      Email: request.email,
      Code: request.code,
    });
  }

  refreshToken(token: string): Observable<AuthModel> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<AuthModel>(
      `${this.API_USERS_URL}/refresh-token`,
      {},
      { headers }
    );
  }

  getCurrentUser(token: string): Observable<User | undefined> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get<User>(`${this.API_USERS_URL}/me`, { headers })
      .pipe(catchError(() => of(undefined)));
  }

  createUser(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(this.API_USERS_URL, user);
  }

  verifyEmail(verificationToken: string): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}verify-email?token=${verificationToken}`,
      verificationToken
    );
  }

  forgotPassword(
    forgotPasswordModel: ForgotPasswordRequest
  ): Observable<ForgotPasswordRequest> {
    return this.http.post<ForgotPasswordRequest>(
      `${this.API_USERS_URL}/forgot-password`,
      forgotPasswordModel
    );
  }

  resetPassword(
    resetPassword: ResetPasswordRequest
  ): Observable<ResetPasswordRequest> {
    return this.http.put<ResetPasswordRequest>(
      `${this.API_USERS_URL}/reset-password`,
      resetPassword
    );
  }
}
